import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/site/SEO';
import {
  ShieldCheck,
  MessageCircle,
  CheckCircle2,
  BadgeCheck,
  Clock3,
  Percent,
  IndianRupee,
  Users,
  Phone,
  Mail,
  MapPin,
  User,
  ArrowRight,
  RotateCw,
  Lock,
  Home as HomeIcon,
  Building2,
  Wallet,
  Briefcase,
  ChevronRight,
  ArrowLeftRight,
  Landmark,
} from 'lucide-react';

const LOAN_TYPES = [
  { id: 'home-loan', label: 'Home Loan', icon: HomeIcon },
  { id: 'lap', label: 'Loan Against Property', icon: Building2 },
  { id: 'business', label: 'Business Loan', icon: Briefcase },
  { id: 'personal', label: 'Personal Loan', icon: Wallet },
  { id: 'working-capital', label: 'OD / CC (Working Capital)', icon: Users },
  { id: 'balance-transfer', label: 'Balance Transfer', icon: ArrowLeftRight },
  { id: 'commercial-loan', label: 'Commercial Loan', icon: Landmark },
];

const initial = {
  name: '',
  mobile: '',
  email: '',
  city: '',
  loanType: 'home-loan',
  loanAmount: '',
  dnd: false,
  terms: false,
};

const ApplyNow = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [leadId, setLeadId] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const inputsRef = useRef([]);

  /* Countdown for OTP resend */
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = 'Please enter your full name';
    if (!/^[6-9]\d{9}$/.test(form.mobile)) e.mobile = 'Enter valid 10-digit Indian mobile';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Enter valid email';
    if (!form.city.trim()) e.city = 'Please enter your city';
    if (!form.loanAmount || Number(form.loanAmount) <= 0) e.loanAmount = 'Enter loan amount';
    if (!form.terms) e.terms = 'You must accept the Terms & Privacy';
    if (!form.dnd) e.dnd = 'DND consent is required to contact you';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const sendOtp = async () => {
    if (!validate()) return;
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await fetch('/api/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: form.mobile }),
      });
      const data = await res.json();
      if (!data.success) {
        setOtpError(data.error || 'OTP bhejne mein problem hui. Dobara try karein.');
        return;
      }
      setOtpVerified(false);
      setStep(2);
      setResendIn(30);
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    } catch {
      setOtpError('Network error. Please check your connection.');
    } finally {
      setOtpSending(false);
    }
  };

  const resend = async () => {
    setOtpSending(true);
    setOtpError('');
    setOtpDigits(['', '', '', '', '', '']);
    try {
      const res = await fetch('/api/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: form.mobile }),
      });
      const data = await res.json();
      if (!data.success) {
        setOtpError(data.error || 'OTP bhejne mein problem hui.');
        return;
      }
      setResendIn(30);
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    } catch {
      setOtpError('Network error. Please check your connection.');
    } finally {
      setOtpSending(false);
    }
  };

  const onOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val;
    setOtpDigits(next);
    setOtpError('');
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onOtpKey = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const onOtpPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setOtpDigits(next);
    inputsRef.current[Math.min(text.length, 5)]?.focus();
  };

  const verify = async () => {
    const entered = otpDigits.join('');
    if (!otpVerified && entered.length < 6) return setOtpError('Please enter all 6 digits');
    setOtpVerifying(true);
    setOtpError('');
    let verified = otpVerified;
    try {
      if (!otpVerified) {
        const res = await fetch('/api/otp/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mobile: form.mobile, code: entered }),
        });
        const data = await res.json();
        if (!data.success) {
          setOtpError(data.error || 'Incorrect OTP. Please try again.');
          return;
        }
        setOtpVerified(true);
        verified = true;
      }
      const id = leadId || 'FIN' + Date.now().toString().slice(-8);
      const record = {
        id,
        ...form,
        loanType: LOAN_TYPES.find((l) => l.id === form.loanType)?.label,
        verifiedAt: new Date().toISOString(),
        source: 'apply-now-cbi',
      };
      const save = await fetch('/api/forms/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_type: 'loan', data: record }),
      });
      if (!save.ok) throw new Error('Could not save your application. Please retry.');
      setLeadId(id);
      setStep(3);
    } catch {
      setOtpError(verified ? 'Your number is verified, but your application could not be saved. Please retry.' : 'Network error. Please check your connection.');
    } finally {
      setOtpVerifying(false);
    }
  };

  const reset = () => {
    setStep(1);
    setForm(initial);
    setErrors({});
    setOtpDigits(['', '', '', '', '', '']);
    setLeadId('');
    setOtpVerified(false);
  };

  const activeLoan = LOAN_TYPES.find((l) => l.id === form.loanType);

  return (
    <main>
      <SEO
        title="Apply Now for Home Loan | Get Instant Quote | Finirio"
        description="Apply now to get an instant home loan quote from 20+ banks. Rates from 7%*, no upper limit, 100% funding, up to 30 years tenure. SMS OTP verification for secure application."
        path="/apply-now-cbi"
        keywords="apply home loan online, home loan quote, home loan application india, otp home loan"
      />

      {/* Banner */}
      <section className="relative overflow-hidden navy-panel">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 py-12 md:py-14 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-[#e8c887]/25 text-[13px] font-medium text-[#f0dcae] mb-4">
              <BadgeCheck className="w-4 h-4" style={{ color: '#2ea690' }} />
              Get an instant quote in under 2 minutes
            </span>
            <h1 className="font-heading text-[32px] md:text-[46px] leading-[1.08] font-bold tracking-tight text-white">
              Apply Now &amp; unlock the <span className="text-gold-grad">best home loan offer</span> for you
            </h1>
            <p className="mt-4 text-white/75 text-[15px] md:text-[16px] leading-relaxed max-w-[600px]">
              Compare offers from 20+ banks in one place. Fill the form and verify your mobile via SMS
              OTP — our advisor will call you back with the best matched offer.
            </p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-[640px]">
              {[
                { i: Percent, t: '7%*', s: 'Starting ROI' },
                { i: IndianRupee, t: 'No Cap', s: 'Loan Amount' },
                { i: Clock3, t: '30 yrs', s: 'Max Tenure' },
                { i: ShieldCheck, t: '100%', s: 'Funding' },
              ].map((x, i) => (
                <div key={i} className="rounded-xl bg-white/[0.08] backdrop-blur border border-white/12 px-3 py-2.5 flex items-center gap-2 hover:border-[#e8c887]/40 transition-colors">
                  <div className="w-9 h-9 rounded-md grid place-items-center flex-none bg-white/10 border border-white/10">
                    <x.i className="w-4 h-4" style={{ color: '#e8c887' }} />
                  </div>
                  <div className="leading-tight">
                    <p className="font-heading font-bold text-white text-[15px]">{x.t}</p>
                    <p className="text-[11px] text-white/60">{x.s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 hidden lg:block">
            <img
              src="/images/family-home.jpg"
              alt="Happy home owner receiving keys"
              className="w-full h-[280px] object-cover rounded-2xl shadow-2xl border border-[#e8c887]/30"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Form section */}
      <section className="py-14 bg-white">
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left info column */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="card-lux overflow-hidden">
              <img
                src="/images/advisor.jpg"
                alt="Finirio advisor helping customer with home loan paperwork"
                className="w-full h-[220px] object-cover"
                loading="lazy"
              />
              <div className="p-5">
                <h3 className="font-heading font-bold text-[18px] text-slate-900 mb-1">
                  Why apply with Finirio?
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  A single application, offers from 20+ leading banks, transparent process and doorstep documentation.
                </p>
                <ul className="space-y-2">
                  {[
                    'Zero advisory fee for customers',
                    'End-to-end handholding till disbursal',
                    'PMAY subsidy assistance where eligible',
                    'RBI-compliant data privacy',
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-[14px] text-slate-700">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-none" style={{ color: '#2ea690' }} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-5 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg grid place-items-center" style={{ backgroundColor: '#e7f6f2' }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: '#2ea690' }} />
                </div>
                <div>
                  <p className="font-heading font-bold text-slate-900">100% Secure &amp; Confidential</p>
                  <p className="text-[13px] text-slate-600">Your data is encrypted and never shared without consent.</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-5 flex items-center gap-4 bg-white">
              <div className="w-12 h-12 rounded-full grid place-items-center" style={{ backgroundColor: '#eaf3fb' }}>
                <Phone className="w-5 h-5" style={{ color: '#1e73be' }} />
              </div>
              <div>
                <p className="text-sm text-slate-600">Prefer to talk?</p>
                <a href="tel:+917972467340" className="font-heading font-bold text-slate-900 hover:text-[#1e73be]">
                  +91 79724 67340
                </a>
              </div>
            </div>
          </aside>

          {/* Right form column */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-100 shadow-sm bg-white overflow-hidden">
              {/* Progress */}
              <div className="px-6 pt-6">
                <div className="flex items-center justify-between mb-1">
                  {['Your Details', 'Verify OTP', 'All Set'].map((s, i) => {
                    const n = i + 1;
                    const active = step === n;
                    const done = step > n;
                    return (
                      <div key={s} className="flex-1 flex items-center">
                        <div
                          className={`w-9 h-9 rounded-full grid place-items-center text-sm font-semibold border ${
                            active
                              ? 'bg-[#1e73be] border-[#1e73be] text-white'
                              : done
                              ? 'bg-[#2ea690] border-[#2ea690] text-white'
                              : 'bg-white border-slate-200 text-slate-500'
                          }`}
                        >
                          {done ? <CheckCircle2 className="w-4 h-4" /> : n}
                        </div>
                        <div className="ml-2 flex-1">
                          <p className={`text-[13px] font-medium ${active || done ? 'text-slate-900' : 'text-slate-500'}`}>
                            {s}
                          </p>
                        </div>
                        {n < 3 && <div className={`h-[2px] flex-1 mx-2 rounded ${done ? 'bg-[#2ea690]' : 'bg-slate-200'}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Step 1: Form */}
              {step === 1 && (
                <div className="p-6 md:p-8">
                  <h2 className="font-heading text-[22px] md:text-[26px] font-bold text-slate-900 mb-1">
                    Tell us about you
                  </h2>
                  <p className="text-slate-600 text-[14px] mb-6">
                    Fill in your details. We&apos;ll send a one-time password via SMS to verify your mobile.
                  </p>

                  {/* Loan type chips */}
                  <div>
                    <label className="block text-slate-700 text-[13px] font-semibold mb-2 uppercase tracking-wide">
                      Loan Type
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {LOAN_TYPES.map((l) => {
                        const active = form.loanType === l.id;
                        return (
                          <button
                            key={l.id}
                            type="button"
                            onClick={() => onChange('loanType', l.id)}
                            className={`px-3 py-2 rounded-full text-[13px] font-medium border transition inline-flex items-center gap-2 ${
                              active
                                ? 'bg-[#1e73be] text-white border-[#1e73be]'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-[#1e73be]/40'
                            }`}
                          >
                            <l.icon className="w-4 h-4" />
                            {l.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FieldInput
                      icon={User}
                      label="Full Name"
                      placeholder="e.g. Rahul Sharma"
                      value={form.name}
                      onChange={(v) => onChange('name', v)}
                      error={errors.name}
                    />
                    <FieldInput
                      icon={Phone}
                      prefix="+91"
                      label="Mobile Number"
                      placeholder="10-digit mobile"
                      inputMode="numeric"
                      maxLength={10}
                      value={form.mobile}
                      onChange={(v) => onChange('mobile', v.replace(/\D/g, ''))}
                      error={errors.mobile}
                    />
                    <FieldInput
                      icon={Mail}
                      label="Email"
                      placeholder="you@example.com"
                      type="email"
                      value={form.email}
                      onChange={(v) => onChange('email', v)}
                      error={errors.email}
                    />
                    <FieldInput
                      icon={MapPin}
                      label="City"
                      placeholder="e.g. Mumbai"
                      value={form.city}
                      onChange={(v) => onChange('city', v)}
                      error={errors.city}
                    />
                    <FieldInput
                      icon={IndianRupee}
                      label="Required Loan Amount"
                      placeholder="e.g. 5000000"
                      inputMode="numeric"
                      value={form.loanAmount}
                      onChange={(v) => onChange('loanAmount', v.replace(/\D/g, ''))}
                      error={errors.loanAmount}
                    />
                    <div>
                      <label className="block text-slate-700 text-[13px] font-semibold mb-2 uppercase tracking-wide">
                        Selected Product
                      </label>
                      <div className="h-11 px-3 border border-slate-200 rounded-md bg-slate-50 flex items-center gap-2 text-slate-800">
                        {activeLoan && <activeLoan.icon className="w-4 h-4" style={{ color: '#1e73be' }} />}
                        <span className="text-[14px] font-medium">{activeLoan?.label}</span>
                      </div>
                    </div>
                  </div>

                  {/* Consents */}
                  <div className="mt-6 space-y-3">
                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${errors.dnd ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}>
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 accent-[#1e73be]"
                        checked={form.dnd}
                        onChange={(e) => onChange('dnd', e.target.checked)}
                      />
                      <span className="text-[13px] text-slate-700 leading-relaxed">
                        <b>DND consent:</b> I authorise Finirio and its representatives to contact me via call, SMS,
                        SMS and email regarding my loan enquiry, even if my number is registered on the DND / NDNC
                        list. This consent overrides any DND registration for this specific enquiry.
                      </span>
                    </label>
                    {errors.dnd && <p className="text-[12px] text-red-600 -mt-2 ml-1">{errors.dnd}</p>}

                    <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${errors.terms ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}>
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 accent-[#1e73be]"
                        checked={form.terms}
                        onChange={(e) => onChange('terms', e.target.checked)}
                      />
                      <span className="text-[13px] text-slate-700 leading-relaxed">
                        I agree to the{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1e73be] underline">Terms &amp; Privacy Policy</a>{' '}
                        and consent to processing of my personal data for loan enquiries.
                      </span>
                    </label>
                    {errors.terms && <p className="text-[12px] text-red-600 -mt-2 ml-1">{errors.terms}</p>}
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                      onClick={sendOtp}
                      disabled={otpSending}
                      className="inline-flex items-center gap-2 px-6 h-12 rounded-md text-white text-[15px] font-semibold hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#1e73be' }}
                    >
                      {otpSending ? <RotateCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      {otpSending ? 'Sending OTP…' : 'Send OTP'}
                    </button>
                    <span className="inline-flex items-center gap-2 text-[13px] text-slate-500">
                      <Lock className="w-4 h-4" /> Your details are 100% secure
                    </span>
                  </div>
                </div>
              )}

              {/* Step 2: OTP */}
              {step === 2 && (
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 rounded-full grid place-items-center" style={{ backgroundColor: '#dcf7e7' }}>
                      <MessageCircle className="w-5 h-5" style={{ color: '#25D366' }} />
                    </div>
                    <div>
                      <h2 className="font-heading text-[22px] md:text-[24px] font-bold text-slate-900">Verify via SMS OTP</h2>
                      <p className="text-slate-600 text-[13px]">
                        We&apos;ve sent a 6-digit OTP via SMS to <b>+91 {form.mobile.replace(/(\d{5})(\d{5})/, '$1 $2')}</b>{' '}
                        <button className="text-[#1e73be] underline text-[13px]" onClick={() => { setOtpVerified(false); setStep(1); }}>
                          Change
                        </button>
                      </p>
                    </div>
                  </div>

                  {otpVerified && <p className="mt-5 text-sm text-emerald-700">Number verified. Your application still needs to be saved.</p>}
                  {!otpVerified && <div className="mt-6 flex gap-2 justify-center md:justify-start">
                    {otpDigits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputsRef.current[i] = el)}
                        value={d}
                        onChange={(e) => onOtpChange(i, e.target.value)}
                        onKeyDown={(e) => onOtpKey(i, e)}
                        onPaste={onOtpPaste}
                        inputMode="numeric"
                        maxLength={1}
                        aria-label={`OTP digit ${i + 1}`}
                        className={`w-12 h-14 md:w-14 md:h-16 text-center text-[22px] font-bold rounded-lg border outline-none transition ${
                          otpError ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-white focus:border-[#1e73be] focus:ring-2 focus:ring-[#1e73be]/20'
                        }`}
                      />
                    ))}
                  </div>}
                  {otpError && <p className="mt-3 text-[13px] text-red-600">{otpError}</p>}

                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <button
                      onClick={verify}
                      disabled={otpVerifying}
                      className="inline-flex items-center gap-2 px-6 h-12 rounded-md text-white text-[15px] font-semibold hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#1e73be' }}
                    >
                      {otpVerifying ? <RotateCw className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      {otpVerifying ? (otpVerified ? 'Saving…' : 'Verifying…') : otpVerified ? 'Retry saving application' : 'Verify & Submit'}
                    </button>
                    {!otpVerified && <button
                      onClick={resend}
                      disabled={resendIn > 0 || otpSending}
                      className={`inline-flex items-center gap-2 text-[14px] font-medium ${
                        resendIn > 0 || otpSending ? 'text-slate-400 cursor-not-allowed' : 'text-[#1e73be] hover:underline'
                      }`}
                    >
                      <RotateCw className={`w-4 h-4 ${otpSending ? 'animate-spin' : ''}`} />
                      {otpSending ? 'Sending…' : resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                    </button>}
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {step === 3 && (
                <div className="p-6 md:p-10 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full grid place-items-center mb-4" style={{ backgroundColor: '#dcf7e7' }}>
                    <CheckCircle2 className="w-10 h-10" style={{ color: '#2ea690' }} />
                  </div>
                  <h2 className="font-heading text-[26px] md:text-[30px] font-bold text-slate-900">
                    Application submitted successfully!
                  </h2>
                  <p className="mt-2 text-slate-600 max-w-[560px] mx-auto">
                    Thanks {form.name.split(' ')[0]}! We&apos;ve recorded your enquiry for a{' '}
                    <b>{LOAN_TYPES.find((l) => l.id === form.loanType)?.label}</b>. Our loan advisor will call
                    you shortly on <b>+91 {form.mobile}</b>.
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-[13px]">
                    <span className="text-slate-500">Your reference ID:</span>
                    <span className="font-mono font-bold text-slate-900">{leadId}</span>
                  </div>
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[720px] mx-auto text-left">
                    {[
                      { i: MessageCircle, t: 'SMS Confirmation', d: 'You will receive a confirmation SMS shortly.' },
                      { i: Users, t: 'Advisor Call', d: 'Our advisor will call within 30 minutes on business hours.' },
                      { i: BadgeCheck, t: 'Best Offer', d: 'Compare offers from 20+ banks and pick the best fit.' },
                    ].map((x, i) => (
                      <div key={i} className="p-4 rounded-lg border border-slate-100 bg-white">
                        <x.i className="w-5 h-5 mb-2" style={{ color: '#1e73be' }} />
                        <p className="font-heading font-bold text-slate-900 text-[15px]">{x.t}</p>
                        <p className="text-[13px] text-slate-600 mt-1">{x.d}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    <button
                      onClick={reset}
                      className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-slate-300 text-slate-800 font-semibold hover:bg-slate-50 transition"
                    >
                      Submit another enquiry
                    </button>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 h-11 rounded-md text-white font-semibold hover:brightness-110 transition"
                      style={{ backgroundColor: '#1e73be' }}
                    >
                      Back to Home <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

const FieldInput = ({ icon: Icon, label, placeholder, value, onChange, type = 'text', prefix, error, ...rest }) => (
  <div>
    <label className="block text-slate-700 text-[13px] font-semibold mb-2 uppercase tracking-wide">
      {label}
    </label>
    <div
      className={`flex items-center rounded-md border transition focus-within:ring-2 focus-within:ring-[#1e73be]/20 overflow-hidden bg-white ${
        error ? 'border-red-300' : 'border-slate-200 focus-within:border-[#1e73be]'
      }`}
    >
      <span className="px-3 h-11 grid place-items-center bg-slate-50 border-r border-slate-200 text-slate-500">
        {Icon && <Icon className="w-4 h-4" />}
      </span>
      {prefix && (
        <span className="px-2 h-11 grid place-items-center text-slate-600 text-[14px] font-medium border-r border-slate-200 bg-slate-50">
          {prefix}
        </span>
      )}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-11 px-3 outline-none text-slate-800 text-[15px]"
        {...rest}
      />
    </div>
    {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}
  </div>
);

export default ApplyNow;
